import { toast } from "sonner";
import { useDeviceStore } from "../store/useDeviceSelectionFromLocator";
import { exportToCSV } from "../utils/exportToCSV";
import { exportComparisonToCSV } from "../utils/exportComparisonToCSV";
import { useDeviceComparison } from "../hooks/useDeviceComparison";

const parseKNumberFromUrl = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    // The FDA query param is uppercase "ID"; also try lowercase as a fallback.
    return (
      parsed.searchParams.get("ID") ||
      parsed.searchParams.get("id") ||
      null
    );
  } catch (_err) {
    console.warn("parseKNumberFromUrl: could not parse URL:", url);
    return null;
  }
};

const DeviceTable = ({ data }) => {
  const { selectedDevices, toggleDevice } = useDeviceStore();
  const { compareDevices, isComparing } = useDeviceComparison();

  const isSelected = (id) => selectedDevices.some((d) => d.id === id);

  const handleCompareAndExport = () => {
    if (selectedDevices.length !== 2) {
      return; // button is disabled — this branch is a safety guard only
    }

    const [d1, d2] = selectedDevices;

    const kNum1 = parseKNumberFromUrl(d1.sourceUrl);
    const kNum2 = parseKNumberFromUrl(d2.sourceUrl);

    if (!kNum1 && !kNum2) {
      toast.error("Could not read K-numbers from either device's source URL. Please check the device data.");
      return;
    }
    if (!kNum1) {
      toast.error(`Could not parse K-number from source URL for "${d1.device || d1.id}".`);
      return;
    }
    if (!kNum2) {
      toast.error(`Could not parse K-number from source URL for "${d2.device || d2.id}".`);
      return;
    }

    compareDevices(kNum1, kNum2, (comparisonResult) => {
      exportComparisonToCSV(comparisonResult);
    });
  };

  const canCompare = selectedDevices.length === 2 && !isComparing;

  return (
    <div className="overflow-x-auto">
      <div className="flex justify-start mb-3 gap-2 flex-wrap items-center">
        <button
          className="btn btn-primary btn-sm"
          onClick={() => exportToCSV(data, "all_predicate_devices.csv")}
        >
          Download All Devices
        </button>

        <button
          className="btn btn-primary btn-sm"
          onClick={handleCompareAndExport}
          disabled={!canCompare}
          title={
            selectedDevices.length < 2
              ? "Select exactly 2 devices to compare"
              : isComparing
              ? "Comparison in progress…"
              : "Compare selected devices and download CSV"
          }
        >
          {isComparing ? (
            <>
              <span className="loading loading-spinner loading-xs mr-1" />
              Comparing…
            </>
          ) : (
            "Compare 2 Selected Devices"
          )}
        </button>

        {/* Live selection counter doubles as a subtle hint */}
        <span className="text-sm text-base-content/60 ml-1">
          {selectedDevices.length} / 2 selected
          {selectedDevices.length === 1 && " — select 1 more to compare"}
        </span>
      </div>

      <table className="table table-zebra w-full text-sm">
        <thead className="bg-base-200">
          <tr>
            <th>Select</th>
            <th>#</th>
            <th>Device</th>
            <th>Product Code</th>
            <th>Classification</th>
            <th>Regulation #</th>
            <th>Applicant</th>
            <th>Decision Date</th>
            <th>Review Days</th>
            <th>Source</th>
          </tr>
        </thead>

        <tbody>
          {data.map((item) => {
            const selected = isSelected(item.id);

            return (
              <tr
                key={item.id}
                className={selected ? "bg-primary/20" : ""}
              >
                <td>
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary checkbox-sm"
                    checked={selected}
                    disabled={!selected && selectedDevices.length >= 2}
                    onChange={() => toggleDevice(item)}
                    aria-label={`Select ${item.id}`}
                  />
                </td>
                <td className="font-mono">{item.id}</td>
                <td>{item.device}</td>
                <td>{item.productCode}</td>
                <td>{item.classification}</td>
                <td>{item.regulationNumber}</td>
                <td>{item.applicant}</td>
                <td>{item.decisionDate}</td>
                <td>{item.reviewDays}</td>
                <td>
                  <a
                    href={item.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link link-primary"
                  >
                    {item.sourceLabel}
                  </a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DeviceTable;
